import { scanProductThunk } from '@redux/features/order.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import BarcodeScannerModal from 'components/BarcodeScannerModal/BarcodeScannerModal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    toteId: string;
    order: any;
    closeViewOrderDetailsModal?: () => void;
    onScanComplete?: () => void; // Callback to refresh order data after scanning
};

const ScanProductModal: FC<IProps> = ({
    show,
    handleClose,
    toteId,
    order,
    closeViewOrderDetailsModal,
    onScanComplete,
}) => {
    const dispatch = useAppDispatch();
    const { scanProductLoading } = useAppSelector((state: RootState) => state.orderState);
    const [availableBatchIds, setAvailableBatchIds] = useState<number[]>([]);
    const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
    const [currentOrder, setCurrentOrder] = useState<any>(order);
    const [showCamera, setShowCamera] = useState(false);

    // Update currentOrder when order prop changes (for real-time updates)
    useEffect(() => {
        if (order) {
            setCurrentOrder(order);
        }
    }, [order]);

    // Close camera scanner when main modal is closed
    useEffect(() => {
        if (!show) setShowCamera(false);
    }, [show]);

    // Extract batchIds from order items (using productBatch.id)
    useEffect(() => {
        if (currentOrder) {
            // Check if order has items array (from getOrderById response)
            if (currentOrder.items && Array.isArray(currentOrder.items)) {
                const batchIds: number[] = [];
                currentOrder.items.forEach((item: any) => {
                    if (item.item?.productBatch?.id) {
                        const batchId = item.item.productBatch.id;
                        if (!batchIds.includes(batchId)) {
                            batchIds.push(batchId);
                        }
                    }
                });
                setAvailableBatchIds(batchIds);
                if (batchIds.length > 0) {
                    setSelectedBatchId(batchIds[0]);
                }
            }
            // Also check if order has order.items (from list response)
            else if (currentOrder.order?.items && Array.isArray(currentOrder.order.items)) {
                const batchIds: number[] = [];
                currentOrder.order.items.forEach((item: any) => {
                    if (item.item?.productBatch?.id) {
                        const batchId = item.item.productBatch.id;
                        if (!batchIds.includes(batchId)) {
                            batchIds.push(batchId);
                        }
                    }
                });
                setAvailableBatchIds(batchIds);
                if (batchIds.length > 0) {
                    setSelectedBatchId(batchIds[0]);
                }
            }
        }
    }, [currentOrder]);

    // Check if reserved products exist (admin can scan if reserved products > 0)
    const canScanProducts = () => {
        if (!currentOrder) return false;
        // Check if order has reservedProducts array
        // First check in order.reservedProducts (from getOrderById response)
        let reservedProducts = currentOrder.reservedProducts;
        // If not found, check in order.order.reservedProducts (from list response)
        if (!reservedProducts || !Array.isArray(reservedProducts)) {
            reservedProducts = currentOrder.order?.reservedProducts;
        }
        // Can scan if there are reserved products
        return reservedProducts && Array.isArray(reservedProducts) && reservedProducts.length > 0;
    };

    const validationSchema = Yup.object({
        productId: Yup.string().required('Product ID is required'),
        quantity: Yup.number()
            .required('Quantity is required')
            .min(1, 'Quantity must be at least 1')
            .integer('Quantity must be an integer'),
        batchId: Yup.number().required('Batch ID is required'),
    });

    const formik = useFormik({
        initialValues: {
            productId: '',
            quantity: 1,
            batchId: selectedBatchId || 0,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            dispatch(
                scanProductThunk({
                    body: {
                        productId: values.productId,
                        quantity: values.quantity,
                        batchId: values.batchId,
                    },
                    toteId: toteId,
                })
            ).then((res: any) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    formik.resetForm();
                    handleClose();
                    // Refresh order data immediately to update UI in real-time
                    if (onScanComplete) {
                        // Call immediately without delay for real-time update
                        onScanComplete();
                    }
                    // Don't close modal automatically - allow admin to scan more products
                    // Only reset the form so they can scan again
                    // Modal will be closed manually or when reserved products reach 0
                }
            });
        },
    });

    // Update batchId when selectedBatchId changes
    useEffect(() => {
        if (selectedBatchId) {
            formik.setFieldValue('batchId', selectedBatchId);
        }
    }, [selectedBatchId]);
    const isDisabled = !canScanProducts();

    useEffect(() => {
        let buffer = '';
        let timer: NodeJS.Timeout | null = null;

        const handleKeyDown = (e: KeyboardEvent) => {
            // تجاهل لو الـ input مش ظاهر أو الـ modal مقفول
            if (!show || isDisabled) return;

            // لو ضغط Enter → نحط الكود جوه الـ input
            if (e.key === 'Enter') {
                if (buffer.trim().length > 0) {
                    formik.setFieldValue('productId', buffer.trim());
                    buffer = '';
                }
                return;
            }

            // نتأكد إن الحرف فعلاً جزء من الـ barcode (حروف أو أرقام)
            // Check if e.key exists and has length === 1 to avoid undefined errors
            if (e.key && e.key.length === 1) {
                buffer += e.key;
                // Reset timer بعد كل key سريع
                if (timer) clearTimeout(timer);
                timer = setTimeout(() => {
                    buffer = ''; // لو وقف الكتابة لمدة >100ms نمسح البافر
                }, 100);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [formik, show, isDisabled]);
    
    if (!show || !toteId) return null;

    // -------------------------------------------------------

    // -------------------------------------------------------

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[90%] md:max-w-[600px]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Scan Product into Tote</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product ID (Barcode)  Or Serial Number*</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                name="productId"
                                value={formik.values.productId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={scanProductLoading || isDisabled}
                                className={`flex-1 min-w-0 px-3 py-2 border rounded-md ${
                                    formik.touched.productId && formik.errors.productId
                                        ? 'border-red-500'
                                        : 'border-gray-300'
                                } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="Enter product barcode"
                            />
                            <ButtonBase
                                type="button"
                                innerText="Open camera"
                                onClick={() => setShowCamera(true)}
                                disabled={scanProductLoading || isDisabled}
                                className="shrink-0 bg-emerald-600 hover:bg-emerald-700 rounded border-none px-3 py-2 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                        </div>
                        {formik.touched.productId && formik.errors.productId && (
                            <p className="text-red-500 text-xs mt-1">{formik.errors.productId}</p>
                        )}
                    </div>
                    <BarcodeScannerModal
                        open={showCamera}
                        onClose={() => setShowCamera(false)}
                        onScan={(code) => {
                            formik.setFieldValue('productId', code.trim());
                            setShowCamera(false);
                        }}
                    />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                        <input
                            type="number"
                            name="quantity"
                            value={formik.values.quantity}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={scanProductLoading || isDisabled}
                            min="1"
                            className={`w-full px-3 py-2 border rounded-md ${
                                formik.touched.quantity && formik.errors.quantity ? 'border-red-500' : 'border-gray-300'
                            } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            placeholder="Enter quantity"
                        />
                        {formik.touched.quantity && formik.errors.quantity && (
                            <p className="text-red-500 text-xs mt-1">{formik.errors.quantity}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1"> Product Batch ID *</label>
                        {availableBatchIds.length > 0 ? (
                            <select
                                name="batchId"
                                value={formik.values.batchId}
                                onChange={(e) => {
                                    const batchId = Number(e.target.value);
                                    setSelectedBatchId(batchId);
                                    formik.setFieldValue('batchId', batchId);
                                }}
                                disabled={scanProductLoading || isDisabled}
                                className={`w-full px-3 py-2 border rounded-md ${
                                    formik.touched.batchId && formik.errors.batchId
                                        ? 'border-red-500'
                                        : 'border-gray-300'
                                } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            >
                                <option value="">Select Product Batch ID</option>
                                {availableBatchIds.map((batchId) => (
                                    <option key={batchId} value={batchId}>
                                        Product Batch ID: {batchId}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="number"
                                name="batchId"
                                value={formik.values.batchId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={scanProductLoading || isDisabled}
                                min="1"
                                className={`w-full px-3 py-2 border rounded-md ${
                                    formik.touched.batchId && formik.errors.batchId
                                        ? 'border-red-500'
                                        : 'border-gray-300'
                                } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="Enter Product Batch ID"
                            />
                        )}
                        {formik.touched.batchId && formik.errors.batchId && (
                            <p className="text-red-500 text-xs mt-1">{formik.errors.batchId}</p>
                        )}
                        {isDisabled && (
                            <p className="text-yellow-600 text-xs mt-1">
                                Cannot scan products: No reserved products found. All reserved products have been
                                fulfilled.
                            </p>
                        )}
                    </div>

                    <div className="flex items-center justify-center gap-2 mt-4">
                        <ButtonBase
                            innerText="Close"
                            type="button"
                            className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                            onClick={handleClose}
                        />
                        <ButtonBase
                            innerText={scanProductLoading ? 'Scanning...' : 'Scan Product'}
                            type="submit"
                            disabled={scanProductLoading || isDisabled}
                            className={`rounded border-none px-4 py-2 text-white ${
                                isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-800'
                            }`}
                        />
                    </div>
                    {!isDisabled && (
                        <p className="text-xs text-center text-gray-500 mt-2">
                            You can scan multiple products. The form will reset after each scan so you can continue
                            scanning until all reserved products are fulfilled.
                        </p>
                    )}
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default ScanProductModal;
